import React, { useState } from "react";
import {
  UserCheck,
  Calendar,
  Filter,
  Download,
  ArrowUpRight,
  Percent,
} from "lucide-react";

const SalesmanPerformanceTable = () => {
  // Data derived from the Executive Summary
  const [performanceData] = useState([
    {
      salesman: "Ahmed",
      leads: 25,
      quotes: 18,
      won: 6,
      lost: 4,
      pipeline: 1250000,
    },
    {
      salesman: "Rahul",
      leads: 20,
      quotes: 14,
      won: 5,
      lost: 3,
      pipeline: 980000,
    },
    {
      salesman: "Sana",
      leads: 15,
      quotes: 11,
      won: 3,
      lost: 2,
      pipeline: 640000,
    },
  ]);

  const [filters, setFilters] = useState({
    salesman: "All",
    startDate: "2025-01-01",
    endDate: "2025-03-31",
  });

  const filteredData = performanceData.filter(
    (item) => filters.salesman === "All" || item.salesman === filters.salesman,
  );

  // Calculation helper
  const calculateRate = (num, total) => ((num / total) * 100).toFixed(1);

  return (
    <div className="p-4 bg-slate-50 min-h-screen font-sans text-slate-900">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-800 uppercase">
            Performance Leaderboard
          </h1>
          <p className="text-slate-500 text-xs">
            Conversion rates & pipeline efficiency per salesperson.
          </p>
        </div>
        <button className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-700 hover:bg-slate-100 transition-all shadow-sm">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Compact Filter Bar */}
      <div className="flex flex-wrap items-center gap-4 mb-6 bg-white p-3 rounded-xl shadow-sm border border-slate-200">
        <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
          <Filter size={14} className="text-slate-400" />
          <select
            className="bg-transparent text-xs font-bold outline-none cursor-pointer"
            onChange={(e) =>
              setFilters({ ...filters, salesman: e.target.value })
            }
          >
            <option value="All">All Personnel</option>
            {performanceData.map((p) => (
              <option key={p.salesman} value={p.salesman}>
                {p.salesman}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
          <Calendar size={14} className="text-slate-400" />
          <input
            type="date"
            value={filters.startDate}
            className="bg-transparent text-xs font-bold outline-none"
            onChange={(e) =>
              setFilters({ ...filters, startDate: e.target.value })
            }
          />
          <span className="text-slate-300 text-xs">—</span>
          <input
            type="date"
            value={filters.endDate}
            className="bg-transparent text-xs font-bold outline-none"
            onChange={(e) =>
              setFilters({ ...filters, endDate: e.target.value })
            }
          />
        </div>
      </div>

      {/* Table List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Salesperson
              </th>
              <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                Leads
              </th>
              <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                Lead → Quote
              </th>
              <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                Win Rate
              </th>
              <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                Lost
              </th>
              <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">
                Pipeline Value
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredData.map((row, idx) => (
              <tr
                key={idx}
                className="hover:bg-slate-50/50 transition-colors group"
              >
                {/* Salesperson */}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xs uppercase">
                      {row.salesman.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-700">
                        {row.salesman}
                      </p>
                      <p className="text-[10px] text-slate-400 uppercase font-medium">
                        Sales Executive
                      </p>
                    </div>
                  </div>
                </td>

                {/* Leads */}
                <td className="px-4 py-4 text-center">
                  <span className="text-sm font-semibold text-slate-600">
                    {row.leads}
                  </span>
                </td>

                {/* Lead to Quote Conversion */}
                <td className="px-4 py-4">
                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-xs font-bold text-blue-600">
                      {calculateRate(row.quotes, row.leads)}%
                    </span>
                    <div className="w-24 bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-400 h-full transition-all duration-700"
                        style={{
                          width: `${calculateRate(row.quotes, row.leads)}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </td>

                {/* Win Rate */}
                <td className="px-4 py-4">
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1 text-emerald-600 font-black text-sm">
                      <span>{calculateRate(row.won, row.leads)}%</span>
                      <ArrowUpRight size={14} />
                    </div>
                    <p className="text-[9px] text-slate-400 uppercase font-bold">
                      {row.won} Deals Won
                    </p>
                  </div>
                </td>

                {/* Lost */}
                <td className="px-4 py-4 text-center">
                  <span className="text-xs font-bold text-red-400 bg-red-50 px-2 py-0.5 rounded-md border border-red-100">
                    {row.lost} Lost
                  </span>
                </td>

                {/* Pipeline Value */}
                <td className="px-4 py-4 text-right">
                  <p className="text-sm font-black text-slate-800">
                    ${row.pipeline.toLocaleString()}
                  </p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">
                    Total Vol.
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesmanPerformanceTable;
